import React, { useRef } from 'react';
import { Button } from "@/components/ui/button"; // Assuming Shadcn Button is available
import { Link } from 'react-router-dom';
import { Palette, Target, Smile, MapPin, LayoutGrid, Code2, Search, Wrench, MessageSquare, PencilRuler, Rocket, Sparkle } from 'lucide-react'; // Import icons
import { motion, useScroll, useTransform } from 'framer-motion'; // Import motion and useScroll
import { Helmet } from 'react-helmet-async'; // Import Helmet
import ClientPortalSection from '../components/ClientPortalSection';

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const HomePage = () => {
  // Ref and hooks for Services parallax
  const servicesSectionRef = useRef(null);
  const { scrollYProgress: scrollYProgressServices } = useScroll({
    target: servicesSectionRef,
    offset: ["start end", "end start"]
  });
  const parallaxY = useTransform(scrollYProgressServices, [0, 1], ["-15%", "15%"]);

  return (
    <>
      <Helmet>
        <title>Professioneel Webdesign Limburg & België | AI Automatisatie | CylostreamWeb</title>
        <meta name="description" content="Op zoek naar modern webdesign in Limburg? CylostreamWeb bouwt snelle, resultaatgerichte websites en biedt AI automatiseringsoplossingen in België." />
        <meta name="keywords" content="webdesign Limburg, website laten maken België, webontwikkeling, AI automatisatie, KMO website, CylostreamWeb, Hasselt" />
        <meta property="og:title" content="Professioneel Webdesign Limburg & België | AI Automatisatie | CylostreamWeb" />
        <meta property="og:description" content="Op zoek naar modern webdesign in Limburg? CylostreamWeb bouwt snelle, resultaatgerichte websites en biedt AI automatiseringsoplossingen in België." />
        {/* og:url will be default from App.tsx */} 
      </Helmet>

      {/* Hero Section - Removed background gradient */}
      <motion.section 
        className="hero-section text-center py-12 md:py-20 overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <div className="relative z-10"> {/* Content container */}
          <motion.h1 
            variants={fadeInUp}
            className="text-4xl md:text-6xl font-bold mb-4"
          >
            Laat Uw Bedrijf Online 
            <span className="relative inline-block mx-2">
              <span className="inline-block bg-gradient-to-r from-secondary to-teal-300 bg-clip-text text-transparent">
                Schitteren
              </span>
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.8, type: 'spring', stiffness: 200 }}
                className="absolute -top-2 -right-3 text-secondary"
              >
                  <Sparkle size={18} fill="currentColor" /> 
              </motion.span>
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 1.0, type: 'spring', stiffness: 200 }}
                className="absolute -bottom-1 -left-2 text-amber-400"
              >
                  <Sparkle size={14} fill="currentColor" />
              </motion.span>
               <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: [0, 1, 0.8, 1], opacity: [0, 1, 1, 1] }}
                transition={{ delay: 1.2, duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
                className="absolute top-0 -left-4 text-secondary opacity-70"
              >
                  <Sparkle size={12} fill="currentColor" />
              </motion.span>
            </span>
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto"
          >
            Van een eerste idee tot een professionele website die bezoekers aantrekt en klanten oplevert. Wij zorgen voor een prachtig ontwerp en een vlekkeloze technische werking, zodat u zich kunt focussen op uw zaak.
          </motion.p>
          <motion.div 
            variants={fadeInUp}
            className="flex justify-center space-x-4"
          >
            <Button asChild size="lg">
              <Link to="/services">Onze Diensten</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/portfolio">Bekijk Portfolio</Link>
            </Button>
          </motion.div>
        </div>
      </motion.section>

      {/* Why Choose Us Section - Moved up here */}
      <motion.section 
        className="why-choose-us py-16 md:py-24 bg-background"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={staggerContainer}
      >
        <div className="container mx-auto px-4">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-center mb-12"
            variants={fadeInUp}
          >
            Waarom <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Kiezen</span> Voor CylostreamWeb?
          </motion.h2>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
          >
            {/* Benefit Item 1 - No card style */}
            <motion.div 
              variants={fadeInUp} 
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="benefit-item text-center p-6"
            >
              <motion.div whileHover={{ scale: 1.15 }}>
                <Palette size={48} className="mx-auto mb-4 text-primary" />
              </motion.div>
              <h3 className="text-xl font-semibold mb-2">Professioneel & Op Maat</h3>
              <p className="text-muted-foreground">
                Websites die perfect passen bij uw merk en doelstellingen.
              </p>
            </motion.div>
            {/* Benefit Item 2 - No card style */}
            <motion.div 
              variants={fadeInUp} 
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="benefit-item text-center p-6"
            >
              <motion.div whileHover={{ scale: 1.15 }}>
                <Target size={48} className="mx-auto mb-4 text-primary" />
              </motion.div>
              <h3 className="text-xl font-semibold mb-2">Resultaatgericht</h3>
              <p className="text-muted-foreground">
                Meer dan een mooi design; we bouwen websites die bezoekers omzetten in klanten.
              </p>
            </motion.div>
            {/* Benefit Item 3 - No card style */}
            <motion.div 
              variants={fadeInUp} 
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="benefit-item text-center p-6"
            >
              <motion.div whileHover={{ scale: 1.15 }}>
                <Smile size={48} className="mx-auto mb-4 text-primary" />
              </motion.div>
              <h3 className="text-xl font-semibold mb-2">Zorgeloos Proces</h3>
              <p className="text-muted-foreground">
                Wij begeleiden u van A tot Z, met duidelijke communicatie en zonder technisch jargon.
              </p>
            </motion.div>
            {/* Benefit Item 4 - No card style */}
            <motion.div 
              variants={fadeInUp} 
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="benefit-item text-center p-6"
            >
              <motion.div whileHover={{ scale: 1.15 }}>
                <MapPin size={48} className="mx-auto mb-4 text-primary" />
              </motion.div>
              <h3 className="text-xl font-semibold mb-2">Lokale Expertise</h3>
              <p className="text-muted-foreground">
                Een partner uit België die de lokale markt begrijpt en dichtbij is.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Client Portal Section - Moved up here */}
      <ClientPortalSection />

      {/* Our Process Section - Adjusted viewport */}
      <motion.section 
        className="our-process py-16 md:py-24 bg-background"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={staggerContainer}
      >
        <div className="container mx-auto px-4">
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-center mb-12">
            Ons <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Proces</span>
          </motion.h2>
          <motion.div 
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center"
          >
            {/* Step 1 */}
            <motion.div variants={fadeInUp} className="process-step">
              <MessageSquare size={48} className="mx-auto mb-4 text-primary"/>
              <h3 className="text-xl font-semibold mb-2">1. Kennismaking</h3>
              <p className="text-muted-foreground text-sm">We bespreken uw doelen, wensen en doelgroep.</p>
            </motion.div>
            {/* Step 2 */}
            <motion.div variants={fadeInUp} className="process-step">
              <PencilRuler size={48} className="mx-auto mb-4 text-primary"/>
              <h3 className="text-xl font-semibold mb-2">2. Ontwerp</h3>
              <p className="text-muted-foreground text-sm">We creëren een visueel ontwerp en vragen uw feedback.</p>
            </motion.div>
            {/* Step 3 */}
            <motion.div variants={fadeInUp} className="process-step">
              <Code2 size={48} className="mx-auto mb-4 text-primary"/> 
              <h3 className="text-xl font-semibold mb-2">3. Ontwikkeling</h3>
              <p className="text-muted-foreground text-sm">We bouwen de website met moderne technologieën.</p>
            </motion.div>
            {/* Step 4 */}
            <motion.div variants={fadeInUp} className="process-step">
              <Rocket size={48} className="mx-auto mb-4 text-primary"/>
              <h3 className="text-xl font-semibold mb-2">4. Lancering</h3>
              <p className="text-muted-foreground text-sm">Na grondig testen zetten we uw nieuwe website live.</p>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Portfolio Teaser & CTA Section - Adjusted viewport */}
      <motion.section 
        className="portfolio-cta py-16 md:py-24 bg-background"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={staggerContainer}
      >
        <div className="container mx-auto px-4 text-center">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            variants={fadeInUp}
          >
            Bekijk Ons <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Werk</span>
          </motion.h2>
          <motion.p 
            className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto"
            variants={fadeInUp} 
            transition={{ delay: 0.1 }}
          >
            Ontdek enkele projecten die we met trots hebben gerealiseerd voor onze klanten.
          </motion.p>
          
          {/* Replace grid with placeholder text */}
          <motion.div 
             className="text-center text-muted-foreground italic mb-12"
             variants={fadeInUp}
             transition={{ delay: 0.2 }}
          >
            Ons portfolio wordt binnenkort bijgewerkt. Kom snel terug!
          </motion.div>
          {/* 
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
            variants={staggerContainer} 
            transition={{ delay: 0.2 }}
          >
            // Placeholder items removed
          </motion.div>
          */}
          
          <motion.div 
            className="flex justify-center space-x-4"
            variants={fadeInUp}
            transition={{ delay: 0.4 }}
          >
            {/* Change button to link directly to placeholder page */}
            <Button asChild size="lg" variant="outline">
              <Link to="/portfolio">Bekijk Portfolio (Binnenkort!)</Link>
            </Button>
            <Button asChild size="lg">
              <Link to="/pricing">Bekijk Prijzen</Link>
            </Button>
          </motion.div>
        </div>
      </motion.section>

      {/* Testimonials Section - Commented out */}
      {/* 
      <motion.section 
        className="testimonials py-16 md:py-24 bg-background"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={staggerContainer}
      >
        <div className="container mx-auto px-4">
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-center mb-12">
            Wat <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Klanten</span> Zeggen
          </motion.h2>
          <motion.div 
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            // Testimonial cards placeholder 
          </motion.div>
        </div>
      </motion.section>
      */}

      {/* Final CTA Section - Changed background */}
      <motion.section 
        className="final-cta py-16 md:py-24 bg-slate-50 text-foreground"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={fadeInUp}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Klaar om Online te <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Groeien</span>?
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Neem vandaag nog contact op voor een vrijblijvend gesprek en ontdek hoe wij uw bedrijf online kunnen helpen slagen.
          </p>
          <Button asChild size="lg" variant="default">
            <Link to="/contact">Start Uw Project</Link>
          </Button>
        </div>
      </motion.section>
    </>
  );
};

export default HomePage;

// Helper CSS for animations (add to your global CSS file, e.g., index.css)
/*
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.animate-fade-in-up {
  animation: fade-in-up 0.6s ease-out forwards;
}
.animation-delay-200 { animation-delay: 0.2s; }
.animation-delay-400 { animation-delay: 0.4s; }
*/ 